import React from 'react';

import { Button } from '@eika/button';

import ANALYTICS_CATEGORIES from '../../analytics/categories';
import { Content } from '../../components/shared/Content';
import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import { useConfig } from '../../defaultConfig';
import { useAccounts } from '../../fetchHooks/useAccount';
import { useOutboxes } from '../../fetchHooks/useOutbox';
import useTranslation from '../../hooks/useTranslation';
import { SigningContextProvider, useSigningContext } from '../../signingContext/signingContext';
import { Actions } from '../../signingContext/signingTypes';
import { ApprovalModal, EmptyListContent, ListContent, ListSkeleton } from './components';
import useOutboxSigning from './hooks/useOutboxSigning';

import './OutboxApprovalList.scss';

const OutboxApprovalList: React.FC = () => {
    const { featureToggles } = useConfig();
    const translated = useTranslation();
    const { data: accounts = [] } = useAccounts();
    const {
        data: {
            domesticPaymentsThatNeedSigningFromYou,
            crossBorderPaymentsThatNeedSigningFromYou,
            paymentsWaitingForOthersToSign,
            numberOfPayments,
        },
        isSuccess,
        error,
        invalidateQuery,
        isRefetching,
        isLoading,
    } = useOutboxes(featureToggles.enableBulkPayment);
    const { dispatch } = useSigningContext();
    const isDataLoaded = isSuccess && !!numberOfPayments;

    const { onApproveOutboxItems, onApproveSingleOutboxItem, showModal } = useOutboxSigning();

    if (!featureToggles.enableOutbox) {
        return <></>;
    }

    if (isSuccess && numberOfPayments === 0) {
        return <EmptyListContent />;
    }

    if (error) {
        return (
            <ErrorCardWrapper exception={error}>
                <Button onClick={invalidateQuery}>{translated.tryAgain}</Button>
            </ErrorCardWrapper>
        );
    }

    if (isRefetching || isLoading || !isSuccess) {
        return (
            <Content className="outbox-approval-list-wrapper">
                <div className="approval-list-item-wrapper">
                    <ListSkeleton count={numberOfPayments ?? 5} />
                </div>
            </Content>
        );
    }

    return (
        <Content className="outbox-approval-list-wrapper">
            <>
                {crossBorderPaymentsThatNeedSigningFromYou.items.length > 0 && (
                    <div className="approval-list-item-wrapper" data-testid="crossborder-outbox-content">
                        {isDataLoaded && (
                            <ListContent
                                accounts={accounts}
                                onApproveOutboxItems={onApproveOutboxItems}
                                onApproveSingleOutboxItem={onApproveSingleOutboxItem('CROSSBORDER')}
                                title={translated.outbox.paymentsYouCanSignCrossBorder}
                                data={crossBorderPaymentsThatNeedSigningFromYou}
                                isRefetching={isRefetching}
                                supportBulkSigning={false}
                            />
                        )}
                    </div>
                )}
                {domesticPaymentsThatNeedSigningFromYou.items.length > 0 && (
                    <div className="approval-list-item-wrapper" data-testid="national-outbox-content">
                        {isDataLoaded && (
                            <ListContent
                                accounts={accounts}
                                onApproveOutboxItems={onApproveOutboxItems}
                                onApproveSingleOutboxItem={onApproveSingleOutboxItem('DOMESTIC')}
                                title={translated.outbox.paymentsYouCanSignDomestic}
                                data={domesticPaymentsThatNeedSigningFromYou}
                                isRefetching={isRefetching}
                            />
                        )}
                    </div>
                )}
                {paymentsWaitingForOthersToSign.items.length > 0 && (
                    <div className="approval-list-item-wrapper" data-testid="waiting-for-signing-content">
                        {isDataLoaded && (
                            <ListContent
                                accounts={accounts}
                                onApproveOutboxItems={onApproveOutboxItems}
                                onApproveSingleOutboxItem={onApproveSingleOutboxItem('DOMESTIC')}
                                title={translated.outbox.paymentsOthersMustSign}
                                data={paymentsWaitingForOthersToSign}
                                supportBulkSigning={false}
                                isRefetching={isRefetching}
                                waitingForSigningFromOthers
                            />
                        )}
                    </div>
                )}
            </>
            <ApprovalModal
                show={showModal}
                hideModal={() => null}
                onRetryManually={() => dispatch({ type: Actions.CLEAR_ERROR })}
            />
        </Content>
    );
};

export default () => (
    <SigningContextProvider analyticsCategory={ANALYTICS_CATEGORIES.BULK_PAYMENT}>
        <OutboxApprovalList />
    </SigningContextProvider>
);
